import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Wordmark } from '../components/brand/Wordmark';
import { apiClient } from '../lib/api';
import { CLIENT_NAME } from '../lib/branding';

type Health = {
  status: 'ok' | 'degraded';
  version: string;
  timestamp: string;
  db: { reachable: boolean; latency_ms: number | null };
};

export function HomePage() {
  const [health, setHealth] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authed, setAuthed] = useState<{ display_name: string; role: string } | null>(null);

  useEffect(() => {
    fetch('/api/v1/health')
      .then((r) => r.json() as Promise<Health>)
      .then(setHealth)
      .catch((err) => setError(String(err)));
    apiClient
      .me()
      .then(setAuthed)
      .catch(() => setAuthed(null));
  }, []);

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16 bg-glacier">
      <div className="w-full max-w-xl text-center">
        <p className="label-uppercase mb-10">— Clarity first —</p>

        <div className="inline-flex flex-col items-start" data-testid="brand-wordmark">
          <Wordmark size="xl" tone="on-light" />
        </div>

        <p className="mt-6 text-ink-secondary text-base font-sans">
          {CLIENT_NAME} · External Asset Manager.
          <br />
          One relationship at a time.
        </p>

        {authed ? (
          <div className="mt-12 card p-6 text-left">
            <p className="label-uppercase mb-4">— Session</p>
            <p className="text-sm text-ink-secondary">
              Logged in as <span className="text-ink font-medium">{authed.display_name}</span> (
              {authed.role})
            </p>
            <Link to="/dashboard" className="btn-primary mt-5 w-full">
              Open dashboard
            </Link>
          </div>
        ) : (
          <div className="mt-12 card p-6 text-left">
            <p className="label-uppercase mb-4">— Sign in</p>
            <p className="text-sm text-ink-secondary mb-4">
              Use your {CLIENT_NAME} account credentials. Two-factor authentication required.
            </p>
            <Link to="/login" className="btn-primary w-full">
              Sign in
            </Link>
          </div>
        )}

        <div className="mt-8 card p-6 text-left">
          <p className="label-uppercase mb-4">— System status</p>
          {error && <p className="text-semantic-error text-sm">{error}</p>}
          {!error && !health && <p className="text-ink-tertiary text-sm">Checking API…</p>}
          {health && (
            <dl className="grid grid-cols-2 gap-y-2 text-sm font-tabular">
              <dt className="text-ink-tertiary">API status</dt>
              <dd
                className={
                  health.status === 'ok' ? 'text-semantic-success' : 'text-semantic-warning'
                }
              >
                {health.status}
              </dd>
              <dt className="text-ink-tertiary">DB reachable</dt>
              <dd>{health.db.reachable ? 'yes' : 'no'}</dd>
              <dt className="text-ink-tertiary">DB latency</dt>
              <dd>{health.db.latency_ms ?? '—'} ms</dd>
              <dt className="text-ink-tertiary">Version</dt>
              <dd>{health.version}</dd>
            </dl>
          )}
        </div>

        <p className="mt-12 text-xs text-ink-tertiary">
          Client Relationship Manager · MVP · Built for {CLIENT_NAME} · Complete discretion.
        </p>
      </div>
    </main>
  );
}
